import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import clinicalReportsService from '../../../services/clinicalReportsService';
import CompanyService from '../../../services/companyService';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useNurseWorkspace } from '../../nurse/nurseWorkspaceContext';
import { MAJOR_COPY } from '../majorCopy';
import Icon from '../../common/Icons';
import { HeadDoctorPageHeader } from '../../head-doctor/ui';
import { todayYmd, daysAgoYmd } from '../../../utils/appTimezone';
import ReportsSubNav from '../../head-doctor/reports/ReportsSubNav';
import { ClinicalReportsContext } from '../../clinical-reports/ClinicalReportsContext';
import { MAJOR_REPORTS_NAV_GROUPS, REPORTS_PERIOD_PRESETS } from './majorReportsNavConfig';
import MajorReportsFilterBar from './MajorReportsFilterBar';
import {
  DEFAULT_MAJOR_REPORTS_ADVANCED_FILTERS,
  buildMajorReportsApiParams,
  buildMajorReportsFilterChips,
  countActiveMajorReportFilters,
  detectActiveAgePreset,
  getActKindLabel,
  isDefaultMajorReportsAdvancedFilters,
  isMajorReportsTabScoped,
  resolveMajorReportsActKind
} from './majorReportsFilters';
import '../../head-doctor/HeadDoctorDashboard.css';
import '../../head-doctor/reports/charts/chartTheme';
import '../MajorPortal.css';

const COPY = MAJOR_COPY.REPORTS;

function detectActivePreset(from, to) {
  const today = todayYmd();
  if (to !== today) return null;
  const match = REPORTS_PERIOD_PRESETS.find((preset) => from === daysAgoYmd(preset.days));
  return match?.key || null;
}

export default function MajorReportsLayout() {
  const { pathname } = useLocation();
  const { workSiteId, workSites } = useNurseWorkspace();
  const [from, setFrom] = useState(daysAgoYmd(30));
  const [to, setTo] = useState(todayYmd());
  const [siteFilter, setSiteFilter] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(DEFAULT_MAJOR_REPORTS_ADVANCED_FILTERS);
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const tabActKind = useMemo(() => resolveMajorReportsActKind(pathname), [pathname]);
  const tabScoped = isMajorReportsTabScoped(pathname);
  const tabScopeLabel = tabActKind ? getActKindLabel(COPY, tabActKind) : '';

  const debouncedQ = useDebouncedValue(advancedFilters.q, 350);
  const debouncedActQ = useDebouncedValue(advancedFilters.act_q, 350);

  const debouncedAdvanced = useMemo(
    () => ({
      ...advancedFilters,
      q: debouncedQ,
      act_q: debouncedActQ
    }),
    [advancedFilters, debouncedQ, debouncedActQ]
  );

  useEffect(() => {
    if (workSiteId) setSiteFilter(String(workSiteId));
  }, [workSiteId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await CompanyService.getActiveCompanies();
        if (!cancelled && res.success) {
          setCompanies(Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setCompanies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activePreset = useMemo(() => detectActivePreset(from, to), [from, to]);
  const activeAgePreset = useMemo(() => detectActiveAgePreset(advancedFilters), [advancedFilters]);

  const reportFilters = useMemo(
    () =>
      buildMajorReportsApiParams({
        from,
        to,
        site_id: siteFilter ? parseInt(siteFilter, 10) : null,
        limit: 20,
        pathname,
        ...debouncedAdvanced
      }),
    [from, to, siteFilter, pathname, debouncedAdvanced]
  );

  const load = useCallback(async () => {
    if (!(workSites || []).length) {
      setData(null);
      setLoading(false);
      setError(COPY.ERROR_NO_SITE);
      return;
    }

    if (!tabScoped) {
      setData(null);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await clinicalReportsService.getOverview(reportFilters);
      if (res.success) {
        setData(res.data);
      } else {
        setData(null);
        setError(res.message || COPY.ERROR_LOAD);
      }
    } catch (error) {
      setData(null);
      setError(error?.message || COPY.ERROR_LOAD);
    } finally {
      setLoading(false);
    }
  }, [reportFilters, workSites, tabScoped]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePreset = useCallback((preset) => {
    setTo(todayYmd());
    setFrom(daysAgoYmd(preset.days));
  }, []);

  const handleAdvancedChange = useCallback((key, value) => {
    setAdvancedFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAgePreset = useCallback((preset) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      age_min: preset.min,
      age_max: preset.max ?? ''
    }));
    setAdvancedOpen(true);
  }, []);

  const handleResetAdvanced = useCallback(() => {
    setAdvancedFilters(DEFAULT_MAJOR_REPORTS_ADVANCED_FILTERS);
  }, []);

  const handleRemoveChip = useCallback((chipKey) => {
    if (chipKey === 'age') {
      setAdvancedFilters((prev) => ({ ...prev, age_min: '', age_max: '' }));
      return;
    }
    if (chipKey === 'tab_scope') return;
    setAdvancedFilters((prev) => ({ ...prev, [chipKey]: '' }));
  }, []);

  const siteLabel = useMemo(() => {
    if (!siteFilter) {
      if (data?.filters?.all_work_sites || (workSites || []).length > 1) {
        return COPY.FILTER_ALL_SITES;
      }
      const onlySite = (workSites || []).length === 1 ? workSites[0] : null;
      return onlySite?.name || COPY.FILTER_SITE;
    }
    const site = (workSites || []).find((s) => String(s.id) === String(siteFilter));
    return site?.name || data?.filters?.site_name || COPY.FILTER_SITE;
  }, [siteFilter, workSites, data?.filters?.site_name, data?.filters?.all_work_sites]);

  const periodLabel = data?.range ? `${data.range.from} → ${data.range.to}` : `${from} → ${to}`;

  const filterChips = useMemo(
    () => buildMajorReportsFilterChips(advancedFilters, companies, COPY, { actKind: tabActKind }),
    [advancedFilters, companies, tabActKind]
  );

  const activeAdvancedCount = countActiveMajorReportFilters(advancedFilters);

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      summary: data?.summary || {},
      copy: COPY,
      refresh: load,
      siteLabel,
      periodLabel,
      activeAdvancedCount,
      hasActiveAdvancedFilters: activeAdvancedCount > 0,
      clearAdvancedFilters: handleResetAdvanced,
      reportFilters: buildMajorReportsApiParams({
        from,
        to,
        site_id: siteFilter ? parseInt(siteFilter, 10) : null,
        limit: 10000,
        pathname,
        ...debouncedAdvanced
      })
    }),
    [
      data,
      loading,
      error,
      load,
      siteLabel,
      periodLabel,
      activeAdvancedCount,
      handleResetAdvanced,
      from,
      to,
      siteFilter,
      pathname,
      debouncedAdvanced
    ]
  );

  return (
    <ClinicalReportsContext.Provider value={value}>
      <div className="hd-reports-page">
        <HeadDoctorPageHeader badge={COPY.BADGE} title={COPY.TITLE} description={COPY.DESCRIPTION} />

        <section
          className={`hd-reports-shell mb-4${loading ? ' is-loading' : ''}`}
          aria-busy={loading}
        >
          <MajorReportsFilterBar
            copy={COPY}
            from={from}
            to={to}
            siteFilter={siteFilter}
            workSites={workSites}
            loading={loading}
            activePreset={activePreset}
            tabScoped={tabScoped}
            tabScopeLabel={tabScopeLabel}
            advancedOpen={advancedOpen}
            advancedFilters={advancedFilters}
            companies={companies}
            activeAgePreset={activeAgePreset}
            onFromChange={setFrom}
            onToChange={setTo}
            onSiteChange={setSiteFilter}
            onPreset={handlePreset}
            onRefresh={load}
            onAdvancedOpenChange={setAdvancedOpen}
            onAdvancedChange={handleAdvancedChange}
            onAgePreset={handleAgePreset}
            onResetAdvanced={handleResetAdvanced}
          />

          <div className="hd-reports-context">
            <span className="hd-reports-context__label">{COPY.CONTEXT_PREFIX}</span>
            <div className="hd-reports-context__chips">
              <span className="hd-reports-context-chip hd-reports-context-chip--site">
                <Icon name="building" size={14} aria-hidden />
                <strong>{siteLabel}</strong>
              </span>
              <span className="hd-reports-context-chip hd-reports-context-chip--period">
                <Icon name="calendar" size={14} aria-hidden />
                {periodLabel}
              </span>
              {tabScoped && tabScopeLabel ? (
                <span className="hd-reports-context-chip hd-reports-context-chip--scope">
                  <Icon name="chartBar" size={14} aria-hidden />
                  {tabScopeLabel}
                </span>
              ) : null}
              {activeAdvancedCount > 0 ? (
                <span className="hd-reports-context-chip hd-reports-context-chip--filters">
                  <Icon name="filter" size={14} aria-hidden />
                  {COPY.CONTEXT_FILTERS(activeAdvancedCount)}
                </span>
              ) : null}
              {loading ? (
                <span className="hd-reports-context-chip hd-reports-context-chip--loading">
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                  {COPY.LOADING_OVERLAY}
                </span>
              ) : null}
            </div>
          </div>

          {!isDefaultMajorReportsAdvancedFilters(advancedFilters) || tabScoped ? (
            filterChips.length > 0 ? (
              <div className="major-reports-filters__chips" aria-label={COPY.FILTER_ACTIVE_ARIA}>
                {filterChips.map((chip) =>
                  chip.readonly ? (
                    <span key={chip.key} className="major-reports-filters__chip major-reports-filters__chip--readonly">
                      {chip.label}
                    </span>
                  ) : (
                    <button
                      key={chip.key}
                      type="button"
                      className="major-reports-filters__chip"
                      onClick={() => handleRemoveChip(chip.key)}
                      title={COPY.FILTER_CHIP_REMOVE}
                    >
                      {chip.label}
                      <span aria-hidden>×</span>
                    </button>
                  )
                )}
              </div>
            ) : null
          ) : null}

          <ReportsSubNav copy={COPY} navGroups={MAJOR_REPORTS_NAV_GROUPS} navIcons={MAJOR_COPY.NAV} />
        </section>

        {error ? (
          <Alert variant="danger" className="mb-3 hd-reports-alert">
            {error}
          </Alert>
        ) : null}

        <div className="hd-reports-outlet">
          <Outlet />
        </div>
      </div>
    </ClinicalReportsContext.Provider>
  );
}
